import { Head } from "@unhead/react";
import { Link } from "react-router-dom";
import marketingChart from "./Marketing Chart.png";

export default function SmartTimingVsSupercharged() {
  const title =
    "SatStacker Smart Timing vs River Supercharged Recurring Buys";

  const description =
    "Compare SatStacker Smart Timing with River Supercharged Recurring Buys. See how each approach handles Bitcoin dips, recurring budgets, execution timing, and fees.";

  const canonical =
    "https://satstacker.app/blog/smart-timing-vs-supercharged";

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    datePublished: "2025-11-04",
    dateModified: "2026-09-22",
    author: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    image: [
      "https://satstacker.app/static/blog-preview.jpg",
    ],
    mainEntityOfPage: canonical,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://satstacker.app/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Smart Timing vs River Supercharged Recurring Buys",
        item: canonical,
      },
    ],
  };

  const faqItems = [
    {
      q: "What is the main difference between Smart Timing and River Supercharged Recurring Buys?",
      a: "River can increase the amount of a scheduled recurring purchase when its dip condition is met. SatStacker Smart Timing keeps the planned recurring budget and changes execution timing inside the buying window.",
    },
    {
      q: "Does River Supercharged spend more money during dips?",
      a: "Yes. The user chooses a supercharge multiplier, and a qualifying dip can increase the scheduled purchase amount. The normal recurring amount still applies when the dip condition is not met.",
    },
    {
      q: "Does SatStacker Smart Timing increase my DCA budget during dips?",
      a: "No. Smart Timing is designed around the budget already assigned to the recurring window. It changes when that planned budget is executed rather than requiring extra capital because Bitcoin fell.",
    },
    {
      q: "Do both products automate Bitcoin recurring buys?",
      a: "Yes, but the automation works differently. River's Supercharged feature modifies the purchase amount at the scheduled execution time when its dip rule is met. SatStacker applies timing rules within a defined recurring buy window.",
    },
    {
      q: "Can either approach guarantee more Bitcoin?",
      a: "No. Future Bitcoin prices are unknown. Both approaches depend on market behavior, and neither can guarantee that its execution will outperform another strategy in every period.",
    },
  ];

  return (
    <main className="min-h-screen bg-zinc-950 text-white">
      <article className="max-w-3xl mx-auto px-5 py-12">
        <Head>
          <title>{title} | SatStacker</title>

          <meta
            name="description"
            content={description}
          />

          <link
            rel="canonical"
            href={canonical}
          />

          <meta property="og:type" content="article" />
          <meta property="og:title" content={title} />
          <meta
            property="og:description"
            content={description}
          />
          <meta property="og:url" content={canonical} />
          <meta property="og:site_name" content="SatStacker" />

          <meta
            name="twitter:card"
            content="summary_large_image"
          />
          <meta
            name="twitter:title"
            content={title}
          />
          <meta
            name="twitter:description"
            content={description}
          />

          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>

          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        </Head>

        <Link
          to="/blog"
          className="text-sm text-zinc-500 hover:text-orange-400"
        >
          ← SatStacker Blog
        </Link>

        <header className="mt-8 mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 mb-4">
            Bitcoin DCA Comparison
          </p>

          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            SatStacker Smart Timing vs
            <span className="block text-orange-400">
              River Supercharged Recurring Buys
            </span>
          </h1>

          <p className="text-sm text-zinc-500 mb-6">
            Originally published November 2025 · Updated September 2026
          </p>

          <p className="text-lg text-zinc-300 leading-8">
            SatStacker and River both offer ways to make a standard recurring
            Bitcoin plan react to price movement, but they solve the problem
            differently. River can increase the amount of a scheduled buy
            during a qualifying dip. SatStacker keeps the planned budget fixed
            and changes execution timing inside the buying window.
          </p>
        </header>

        <section className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5 mb-10">
          <p className="text-xs uppercase tracking-widest text-orange-400 font-semibold mb-2">
            Disclosure
          </p>

          <p className="text-zinc-400 leading-7 m-0">
            SatStacker publishes this comparison and is one of the products
            discussed. River product details below are based on River's
            published documentation available when this article was updated.
            Features and pricing can change.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            The difference in one sentence
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                River Supercharged
              </p>

              <h3 className="font-semibold text-white mb-2">
                Increase the buy amount during a qualifying dip
              </h3>

              <p className="text-sm text-zinc-400 leading-6 m-0">
                The recurring order still executes on its scheduled cadence,
                but the amount can increase when River's dip condition is met.
              </p>
            </div>

            <div className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5">
              <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">
                SatStacker Smart Timing
              </p>

              <h3 className="font-semibold text-white mb-2">
                Keep the budget fixed and vary execution timing
              </h3>

              <p className="text-sm text-zinc-400 leading-6 m-0">
                The recurring budget is set first, and Smart Timing can move
                execution inside the defined buying window instead of
                requiring additional capital.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            How River Supercharged Recurring Buys work
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            River's Supercharged Recurring Buys start with a normal recurring
            Bitcoin order. The user selects a recurring amount, a schedule,
            and a supercharge multiplier.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            At the scheduled execution time, River compares Bitcoin's price
            with a moving average associated with the selected cadence. If
            Bitcoin is approximately 1% or more below that moving average,
            the scheduled order uses the selected supercharged amount instead
            of the normal recurring amount.
          </p>

          <p className="text-zinc-400 leading-7">
            River currently documents supercharge multiplier choices that can
            increase the recurring purchase by 25%, 50%, 100%, or 200%.
            Because the larger order uses additional dollars, a supercharged
            month can involve more total Bitcoin spending than the base
            recurring plan.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            How SatStacker Smart Timing works
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            SatStacker starts with the amount you already planned to allocate
            to the recurring buying window. Smart Timing then applies
            execution rules inside that window rather than automatically
            spending the entire allocation when the window opens.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            That means the comparison is not "normal budget versus extra
            dip-buying money." The planned recurring budget remains the basis
            of the strategy.
          </p>

          <Link
            to="/smart-dca"
            className="text-orange-400 hover:underline"
          >
            Read the full Smart DCA guide →
          </Link>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Smart Timing vs Supercharged: side-by-side
          </h2>

          <div className="overflow-x-auto">
            <div className="min-w-[680px] overflow-hidden rounded-2xl border border-zinc-800">
              {[
                [
                  "",
                  "River Supercharged",
                  "SatStacker Smart Timing",
                ],
                [
                  "Base strategy",
                  "Recurring Bitcoin buy",
                  "Recurring Bitcoin buy",
                ],
                [
                  "What changes?",
                  "Purchase amount",
                  "Execution timing inside the window",
                ],
                [
                  "Dip response",
                  "Can spend more",
                  "Uses the planned window budget",
                ],
                [
                  "Execution",
                  "At the scheduled recurring-buy time",
                  "Within a defined buy window",
                ],
                [
                  "Extra capital required for dip response?",
                  "Yes, when supercharged",
                  "No",
                ],
                [
                  "Primary idea",
                  "Buy more when River's dip condition is met",
                  "Look for better execution with the planned budget",
                ],
              ].map((row, rowIndex) => (
                <div
                  key={row[0] || "header"}
                  className={`grid grid-cols-3 ${
                    rowIndex === 0
                      ? "bg-orange-500/10"
                      : rowIndex % 2 === 0
                      ? "bg-zinc-900/50"
                      : "bg-zinc-950"
                  } ${
                    rowIndex < 6
                      ? "border-b border-zinc-800"
                      : ""
                  }`}
                >
                  {row.map((cell, cellIndex) => (
                    <div
                      key={`${rowIndex}-${cellIndex}`}
                      className={`px-4 py-3 text-sm leading-6 ${
                        rowIndex === 0 || cellIndex === 0
                          ? "font-semibold text-zinc-200"
                          : "text-zinc-400"
                      }`}
                    >
                      {cell}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Budget behavior is the biggest difference
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            Consider a hypothetical $100 recurring order.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            With River Supercharged, a qualifying dip can turn that scheduled
            order into a larger purchase based on the multiplier you selected.
            That can be useful for someone who intentionally wants to commit
            additional cash when River's condition is triggered.
          </p>

          <p className="text-zinc-400 leading-7">
            With SatStacker Smart Timing, the planned amount for the window
            remains the planned amount. The system is trying to improve how
            that budget is executed rather than increase how much you spend
            because a dip occurred.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            How the dip logic differs
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            River evaluates the dip condition at the scheduled recurring-buy
            time. Its published documentation compares the current Bitcoin
            price with a moving average whose length depends on the recurring
            schedule.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            SatStacker instead works with a defined buying window. Smart
            Timing can observe price movement after the window opens and
            execute according to its rules before that window closes.
          </p>

          <p className="text-zinc-400 leading-7">
            Neither approach knows Bitcoin's future price. A later price can
            be lower or higher, so neither structure guarantees a better
            outcome in every cycle.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Fees and pricing
          </h2>

          <div className="space-y-4">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
              <h3 className="font-semibold text-white mb-2">
                River
              </h3>

              <p className="text-zinc-400 leading-7 m-0">
                River currently states that recurring and supercharged
                recurring buys become zero-fee after the initial qualifying
                period. The initial recurring order can still be subject to
                River's standard order fee.
              </p>
            </div>

            <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-5">
              <h3 className="font-semibold text-white mb-2">
                SatStacker
              </h3>

              <p className="text-zinc-400 leading-7 m-0">
                Smart Timing is currently $0 for the first month, then
                $1.99 per month plus 0.25% per trade. SatStacker connects
                to an existing Strike account.
              </p>
            </div>
          </div>

          <p className="text-xs text-zinc-500 leading-6 mt-4">
            Pricing and product terms can change. Confirm current pricing
            with the provider before making a decision.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            What about self-custody?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            Custody is separate from the timing logic being compared here.
            River offers Bitcoin withdrawal and auto-withdrawal features.
            SatStacker itself does not custody user funds; it operates through
            a connected Strike account.
          </p>

          <p className="text-zinc-400 leading-7">
            If self-custody is important to you, compare the withdrawal and
            wallet workflow separately from the recurring-buy strategy.
          </p>

          <Link
            to="/articles/self-custody"
            className="text-orange-400 hover:underline"
          >
            Read the Bitcoin self-custody guide →
          </Link>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Historical Smart Timing example
          </h2>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 text-center">
            <img
              src={marketingChart}
              alt="Historical Bitcoin DCA comparison showing scheduled buys and SatStacker Smart Timing"
              className="rounded-xl mx-auto w-full"
            />

            <p className="text-sm text-zinc-500 mt-3 mb-0">
              Historical SatStacker simulation. This chart is not a
              head-to-head River backtest and should not be interpreted as
              one. Past performance does not guarantee future results.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Which type of automation are you actually looking for?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            The useful distinction is less about which feature sounds
            "smarter" and more about what you want the automation to change.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
              <h3 className="font-semibold text-white mb-2">
                River's approach may fit someone who wants
              </h3>

              <ul className="space-y-2 text-sm text-zinc-400 leading-6 list-disc pl-5 m-0">
                <li>A recurring Bitcoin plan directly at River</li>
                <li>Automatic increases in buy size during qualifying dips</li>
                <li>Zero-fee recurring purchases after River's initial period</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5">
              <h3 className="font-semibold text-white mb-2">
                SatStacker's approach may fit someone who wants
              </h3>

              <ul className="space-y-2 text-sm text-zinc-400 leading-6 list-disc pl-5 m-0">
                <li>To keep the planned DCA budget fixed</li>
                <li>Flexible execution inside each buying window</li>
                <li>Automation layered onto an existing Strike account</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-5">
            Smart Timing vs River Supercharged FAQ
          </h2>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <div
                key={item.q}
                className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5"
              >
                <h3 className="font-semibold text-white mb-2">
                  {item.q}
                </h3>

                <p className="text-zinc-400 leading-7 m-0">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-6 mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 font-semibold mb-2">
            Test SatStacker's approach
          </p>

          <h2 className="text-2xl font-bold text-white mt-0 mb-3">
            Compare Smart Timing with regular DCA
          </h2>

          <p className="text-zinc-400 leading-7 mb-5">
            Use your normal recurring amount and cadence to run a historical
            SatStacker simulation before creating an account.
          </p>

          <Link
            to="/calculator"
            className="inline-block rounded-xl bg-orange-500 px-5 py-3 font-bold text-white hover:bg-orange-600"
          >
            Try the Bitcoin DCA Calculator →
          </Link>
        </section>

        <section className="border-t border-zinc-800 pt-8 mb-10">
          <p className="font-semibold text-white mb-4">
            Continue learning
          </p>

          <div className="space-y-3">
            <Link
              to="/smart-dca"
              className="block text-orange-400 hover:underline"
            >
              Smart DCA Guide →
            </Link>

            <Link
              to="/blog/smart-dca-vs-regular-dca"
              className="block text-orange-400 hover:underline"
            >
              Smart DCA vs Regular DCA Case Study →
            </Link>

            <Link
              to="/bitcoin-dca"
              className="block text-orange-400 hover:underline"
            >
              How to DCA Bitcoin →
            </Link>

            <Link
              to="/blog/best-bitcoin-apps-2026"
              className="block text-orange-400 hover:underline"
            >
              Best Bitcoin DCA Apps →
            </Link>
          </div>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">
            River sources checked September 2026
          </p>

          <div className="space-y-2 text-sm">
            <a
              href="https://support.river.com/hc/en-us/articles/50061674534675-What-are-supercharged-recurring-buys"
              target="_blank"
              rel="noreferrer"
              className="block text-orange-400 hover:underline"
            >
              River: What are Supercharged Recurring Buys? →
            </a>

            <a
              href="https://support.river.com/hc/en-us/articles/50055900679315-What-are-River-s-fees"
              target="_blank"
              rel="noreferrer"
              className="block text-orange-400 hover:underline"
            >
              River: Current fee structure →
            </a>

            <a
              href="https://support.river.com/hc/en-us/articles/50061352349715-What-are-recurring-buys"
              target="_blank"
              rel="noreferrer"
              className="block text-orange-400 hover:underline"
            >
              River: Recurring buys →
            </a>
          </div>
        </section>
      </article>
    </main>
  );
}
